import { c as _c } from "react/compiler-runtime";
import { useTranslationContext } from 'platform/components';
import { memo } from 'react';
import type { EagColumn } from '../../../../types/EagColumn';
import type { EagColumnOrGroup } from '../../../../types/EagColumnGroup';
import type { EagFilterProps } from '../../../../types/EagFilterProps';
import type { FilterState } from '../../../../types/FilterState';
import { renderFilter } from '../../../../utils/renderFilter';
import { getVisibleFilterColumns } from './utils/getVisibleFilterColumns';
const FULL_WIDTH_FILTER_TYPES = new Set(['DATE_RANGE', 'DATE_TIME_RANGE', 'SLIDER_RANGE', 'RANGE']);
type FilterContentAreaProps = {
  columnConfig: EagColumnOrGroup[];
  selectedCategoryId: string | null;
  filterState: FilterState;
  onFilterChange: (key: string, value: unknown) => void;
  searchTerm: string;
  noScroll?: boolean; // NEW — disables internal scroll for mobile drawer
};
type FilterItemProps = {
  column: EagColumn;
  value: FilterState;
  onFilterChange: (key: string, value: unknown) => void;
};
const FilterItem = memo(props => {
  const $ = _c(8);
  let t0;
  if ($[0] !== props) {
    t0 = value => props.onFilterChange(props.column.key, value);
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const onChange = t0;
  let t1;
  if ($[2] !== onChange || $[3] !== props.column || $[4] !== props.value) {
    const filterProps = {
      value: (props.value ?? {}) as FilterState,
      onChange,
      isDisabled: false,
      column: props.column
    };
    let t2;
    if ($[6] !== props.column.headerName) {
      t2 = <label className="text-xs text-gray-500 font-medium truncate">{props.column.headerName}</label>;
      $[6] = props.column.headerName;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    t1 = <div className="flex flex-col gap-1" data-filter={props.column.key}>{t2}{renderFilter(props.column, filterProps)}</div>;
    $[2] = onChange;
    $[3] = props.column;
    $[4] = props.value;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
});

/**
 * Right content area of the 75vw FilterPanel.
 *
 * Renders filter widgets for the currently selected category in a responsive
 * 2-column grid.  Wider filter types (DateRange, DateTimeRange, SliderRange,
 * Range) automatically span both columns so their inputs have enough space.
 *
 * ## Category resolution
 *
 * When `selectedCategoryId` matches a group's `groupId`, all instances of that
 * group in `columnConfig` are collected (EagGrid allows the same `groupId` to
 * appear multiple times as a way to organise columns across multiple groups).
 * The union of their children forms the filterable column set.
 *
 * The special virtual id `'__standalone__'` collects all top-level columns
 * that are not part of any group.
 *
 * ## Search
 *
 * When `searchTerm` is non-empty, filter items whose `headerName` does not
 * contain the term (case-insensitive) are hidden.
 */
export function FilterContentArea(props) {
  const $ = _c(29);
  const t = useTranslationContext();
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== props.columnConfig || $[1] !== props.filterState || $[2] !== props.noScroll || $[3] !== props.onFilterChange || $[4] !== props.searchTerm || $[5] !== props.selectedCategoryId || $[6] !== t) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const visibleFilterColumns = getVisibleFilterColumns(props.columnConfig, props.selectedCategoryId, props.searchTerm);
      if (!props.selectedCategoryId) {
        let t4;
        if ($[11] !== t) {
          t4 = t("page.dataGrid.filter.selectCategoryPrompt");
          $[11] = t;
          $[12] = t4;
        } else {
          t4 = $[12];
        }
        let t5;
        if ($[13] !== t4) {
          t5 = <div className="flex items-center justify-center h-full text-sm text-gray-400 p-8">{t4}</div>;
          $[13] = t4;
          $[14] = t5;
        } else {
          t5 = $[14];
        }
        t3 = t5;
        break bb0;
      }
      if (visibleFilterColumns.length === 0) {
        let t4;
        if ($[15] !== props.searchTerm || $[16] !== t) {
          t4 = props.searchTerm ? t("page.dataGrid.filter.noFiltersMatchSearch") : t("page.dataGrid.filter.noFiltersAvailable");
          $[15] = props.searchTerm;
          $[16] = t;
          $[17] = t4;
        } else {
          t4 = $[17];
        }
        let t5;
        if ($[18] !== t4) {
          t5 = <div className="flex items-center justify-center h-full text-sm text-gray-400 p-8">{t4}</div>;
          $[18] = t4;
          $[19] = t5;
        } else {
          t5 = $[19];
        }
        t3 = t5;
        break bb0;
      }
      t2 = props.noScroll ? "p-4" : "p-4 overflow-y-auto h-full";
      t0 = "grid grid-cols-2 gap-4";
      let t4;
      if ($[20] !== props.filterState || $[21] !== props.onFilterChange) {
        t4 = column => {
          const isFullWidth = column.filter && FULL_WIDTH_FILTER_TYPES.has(column.filter.type);
          return <div key={column.key} className={isFullWidth ? "col-span-2" : "col-span-1"}><FilterItem column={column} value={props.filterState[column.key] as FilterState} onFilterChange={props.onFilterChange} /></div>;
        };
        $[20] = props.filterState;
        $[21] = props.onFilterChange;
        $[22] = t4;
      } else {
        t4 = $[22];
      }
      t1 = visibleFilterColumns.map(t4);
    }
    $[0] = props.columnConfig;
    $[1] = props.filterState;
    $[2] = props.noScroll;
    $[3] = props.onFilterChange;
    $[4] = props.searchTerm;
    $[5] = props.selectedCategoryId;
    $[6] = t;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t0 = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[23] !== t0 || $[24] !== t1) {
    t4 = <div className={t0}>{t1}</div>;
    $[23] = t0;
    $[24] = t1;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  let t5;
  if ($[26] !== t2 || $[27] !== t4) {
    t5 = <div className={t2}>{t4}</div>;
    $[26] = t2;
    $[27] = t4;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  return t5;
}